import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Users, Shield, UserCheck, UserX } from 'lucide-react';

const ROLE_COLORS = {
  super_admin: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  company_admin: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  manager: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  agent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
};

export default function UserManagement() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'agent', department: '' });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Fill required fields');
      return;
    }
    try {
      await api.post('/auth/register', newUser);
      toast.success('User created');
      setShowCreate(false);
      setNewUser({ name: '', email: '', password: '', role: 'agent', department: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const toggleActive = async (userId, currentActive) => {
    try {
      await api.put(`/users/${userId}`, { is_active: !currentActive });
      toast.success(currentActive ? 'User deactivated' : 'User activated');
      fetchUsers();
    } catch {
      toast.error('Failed to update user');
    }
  };

  const changeRole = async (userId, role) => {
    try {
      await api.put(`/users/${userId}`, { role });
      toast.success('Role updated');
      fetchUsers();
    } catch {
      toast.error('Failed to update role');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground tracking-tight">
            {user?.role === 'manager' ? 'Team' : 'Users'}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{users.length} members</p>
        </div>
        {(user?.role === 'company_admin' || user?.role === 'super_admin') && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-voyage-blue hover:bg-voyage-blue-dark text-white" data-testid="create-user-btn">
                <Plus className="w-4 h-4" /> Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">Create New User</DialogTitle><DialogDescription>Add a new team member to this company.</DialogDescription></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4" data-testid="create-user-form">
                <div><Label>Full Name *</Label><Input value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} className="mt-1" data-testid="user-name-input" /></div>
                <div><Label>Email *</Label><Input type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} className="mt-1" data-testid="user-email-input" /></div>
                <div><Label>Password *</Label><Input type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} className="mt-1" data-testid="user-password-input" /></div>
                <div>
                  <Label>Role</Label>
                  <Select value={newUser.role} onValueChange={v => setNewUser({...newUser, role: v})}>
                    <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {user?.role === 'super_admin' && <SelectItem value="company_admin">Company Admin</SelectItem>}
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="agent">Agent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Department</Label><Input value={newUser.department} onChange={e => setNewUser({...newUser, department: e.target.value})} className="mt-1" placeholder="e.g., Domestic, International" /></div>
                <Button type="submit" className="w-full bg-voyage-blue hover:bg-voyage-blue-dark text-white" data-testid="submit-user-btn">Create User</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Card className="border border-border bg-card">
        <CardContent className="p-0">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow>
                <TableHead className="font-heading">Name</TableHead>
                <TableHead className="font-heading">Email</TableHead>
                <TableHead className="font-heading">Role</TableHead>
                <TableHead className="font-heading">Department</TableHead>
                <TableHead className="font-heading">Status</TableHead>
                <TableHead className="font-heading text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    {(user?.role === 'super_admin' || user?.role === 'company_admin') && u.id !== user?.id ? (
                      <Select value={u.role} onValueChange={(r) => changeRole(u.id, r)}>
                        <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {user?.role === 'super_admin' && <SelectItem value="company_admin">Company Admin</SelectItem>}
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="agent">Agent</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`${ROLE_COLORS[u.role]} text-xs capitalize`}>{u.role?.replace('_', ' ')}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{u.department || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${u.is_active !== false ? 'text-emerald-600' : 'text-red-500'}`}>
                      {u.is_active !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {u.id !== user?.id && (user?.role === 'super_admin' || user?.role === 'company_admin') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActive(u.id, u.is_active !== false)}
                        className={u.is_active !== false ? 'text-red-500 hover:text-red-600' : 'text-emerald-500 hover:text-emerald-600'}
                        data-testid={`toggle-user-${u.id}`}
                      >
                        {u.is_active !== false ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
